import React from 'react';
import { Vehicle } from '../utils/mockData';
import { Badge } from './ui/Badge';
import { format } from 'date-fns';
import { Clock, MapPin, AlertTriangle, History, Shield } from 'lucide-react';
interface VehicleDrawerProps {
  vehicle: Vehicle;
}
export function VehicleDrawer({
  vehicle
}: VehicleDrawerProps) {
  return <div className="space-y-8" data-id="element-408">
      {/* Header Profile */}
      <div className="flex items-start gap-4" data-id="element-409">
        <div className="h-20 w-20 overflow-hidden rounded-lg border border-gray-700 bg-gray-800" data-id="element-410">
          <img src={vehicle.thumbnailUrl} alt="Vehicle" className="h-full w-full object-cover" data-id="element-411" />
        </div>
        <div data-id="element-412">
          <h1 className="text-2xl font-bold text-white" data-id="element-413">{vehicle.plate}</h1>
          <div className="flex items-center gap-2 mt-1" data-id="element-414">
            <Badge variant="neutral" data-id="element-415">{vehicle.type}</Badge>
            <span className="text-sm text-gray-400" data-id="element-416">
              Driver: {vehicle.driverName}
            </span>
          </div>
        </div>
      </div>

      {/* Warnings */}
      {vehicle.hasWarning && <div className="rounded-lg border border-amber-500/20 bg-amber-500/10 p-4" data-id="element-417">
          <div className="flex items-center gap-2 text-amber-500 mb-1" data-id="element-418">
            <AlertTriangle className="h-4 w-4" data-id="element-419" />
            <span className="font-semibold text-sm" data-id="element-420">Active Warning</span>
          </div>
          <p className="text-sm text-gray-300" data-id="element-421">{vehicle.warningReason}</p>
        </div>}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-4" data-id="element-422">
        <div className="rounded-lg bg-gray-900 p-4 border border-gray-800" data-id="element-423">
          <div className="flex items-center gap-2 text-gray-400 mb-1" data-id="element-424">
            <History className="h-4 w-4" data-id="element-425" />
            <span className="text-xs" data-id="element-426">Total Visits</span>
          </div>
          <div className="text-xl font-bold text-white" data-id="element-427">
            {vehicle.visitCount}
          </div>
        </div>
        <div className="rounded-lg bg-gray-900 p-4 border border-gray-800" data-id="element-428">
          <div className="flex items-center gap-2 text-gray-400 mb-1" data-id="element-429">
            <Clock className="h-4 w-4" data-id="element-430" />
            <span className="text-xs" data-id="element-431">Avg Time Inside</span>
          </div>
          <div className="text-xl font-bold text-white" data-id="element-432">2h 15m</div>
        </div>
      </div>

      {/* Timeline */}
      <div data-id="element-433">
        <h3 className="text-sm font-semibold text-white mb-4" data-id="element-434">
          Current Session Timeline
        </h3>
        <div className="relative space-y-6 pl-4 border-l border-gray-800 ml-2" data-id="element-435">
          {/* Entry */}
          <div className="relative" data-id="element-436">
            <span className="absolute -left-[21px] top-1 h-2.5 w-2.5 rounded-full bg-emerald-500 ring-4 ring-[#1a1d23]" data-id="element-437" />
            <div className="flex flex-col" data-id="element-438">
              <span className="text-xs text-gray-500" data-id="element-439">
                {format(vehicle.entryTime, 'HH:mm')}
              </span>
              <span className="text-sm font-medium text-white" data-id="element-440">
                Entered via {vehicle.gate}
              </span>
              <span className="text-xs text-gray-400" data-id="element-441">
                Confidence: {vehicle.confidence}%
              </span>
            </div>
          </div>

          {/* Bay Arrival (Mock) */}
          {vehicle.bay && <div className="relative" data-id="element-442">
              <span className="absolute -left-[21px] top-1 h-2.5 w-2.5 rounded-full bg-gray-600 ring-4 ring-[#1a1d23]" data-id="element-443" />
              <div className="flex flex-col" data-id="element-444">
                <span className="text-xs text-gray-500" data-id="element-445">
                  {format(new Date(vehicle.entryTime.getTime() + 15 * 60000), 'HH:mm')}
                </span>
                <span className="text-sm font-medium text-white" data-id="element-446">
                  Arrived at {vehicle.bay}
                </span>
              </div>
            </div>}

          {/* Current Status */}
          <div className="relative" data-id="element-447">
            <span className="absolute -left-[21px] top-1 h-2.5 w-2.5 rounded-full bg-amber-500 ring-4 ring-[#1a1d23] animate-pulse" data-id="element-448" />
            <div className="flex flex-col" data-id="element-449">
              <span className="text-xs text-gray-500" data-id="element-450">Now</span>
              <span className="text-sm font-medium text-white" data-id="element-451">
                Currently Inside
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="pt-4 border-t border-gray-800" data-id="element-452">
        <h3 className="text-sm font-semibold text-white mb-3" data-id="element-453">
          Security Actions
        </h3>
        <div className="space-y-2" data-id="element-454">
          <button className="w-full flex items-center justify-between rounded-md bg-gray-900 px-4 py-3 text-sm text-gray-300 hover:bg-gray-800 hover:text-white transition-colors" data-id="element-455">
            <span className="flex items-center gap-2" data-id="element-456">
              <Shield className="h-4 w-4" data-id="element-457" />
              Add to Watchlist
            </span>
            <div className="h-4 w-8 rounded-full bg-gray-700" data-id="element-458" />
          </button>
          <button className="w-full flex items-center gap-2 rounded-md border border-gray-700 px-4 py-3 text-sm text-gray-300 hover:bg-gray-800 hover:text-white transition-colors" data-id="element-459">
            <AlertTriangle className="h-4 w-4" data-id="element-460" />
            Report Incident
          </button>
        </div>
      </div>
    </div>;
}